<template>
  <div
    v-if="isLocalOrDevEnv() && AuthService.isAuthenticated"
    class="d-flex flex-column flex-fill"
    :style="{
      maxHeight: '460px'
    }"
  >
    <!-- LOADING -->
    <v-card-text
      v-if="isLoading"
      class="d-flex flex-column justify-center align-center px-0"
    >
      <DeepLoaderAnimation />
    </v-card-text>
    <v-card-text
      v-else-if="summarizationError"
      class="d-flex flex-column justify-center align-center px-0"
    >
      <!-- TODO: display better error -->
      <slot :error="summarizationError" name="error">
        {{ summarizationError }}
      </slot>
    </v-card-text>

    <template v-else>
      <!-- ACTIONS -->
      <v-card-item
        v-if="summarization?.result"
        class="px-0 flex-grow-0"
        density="compact"
      >
        <div class="d-flex align-center justify-end">
          <DeepASummarizeDocumentContentSettingsMenu
            v-model="settingsMenu"
            v-model:settings="settings"
            @click:apply="summarize"
          />
          <DeepBtnCopyToClipboard
            v-if="summarization && summarization.result"
            :text="summarization.result"
          />
        </div>
      </v-card-item>

      <!-- CONTENT -->
      <v-card-text
        ref="summarizationContainerRef"
        class="d-flex flex-column justify-center align-center px-0 overflow-auto"
      >
        <DeepLoaderAnimation v-if="isLoading" />
        <!-- TODO: display better error -->
        <div v-else-if="summarizationError">
          <slot :error="summarizationError" name="error">
            {{ summarizationError }}
          </slot>
        </div>
        <div v-else-if="summarization?.result">
          <slot
            name="summary"
            :retry="summarize"
            :summarization="summarization"
          >
            <DeepMarkdownRenderer :source="summarization?.result" />
          </slot>
        </div>
      </v-card-text>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  DeepBtnCopyToClipboard,
  DeepLoaderAnimation,
  DeepMarkdownRenderer
} from '@deepcloud/deep-ui-lib';
import { nextTick, type PropType, ref, useTemplateRef, watch } from 'vue';
import type { Document } from '@/types/Document.ts';
import { useI18n } from 'vue-i18n';
import type { DeepAResponseString } from '@deepcloud/api-deep-a';
import { useApi } from '@/composables/useApi.ts';
import AuthService from '@/services/authService.ts';
import { isLocalOrDevEnv } from '@/utils/env.ts';
import DeepASummarizeDocumentContentSettingsMenu from '@/components/deepa/DeepASummarizeDocumentContentSettingsMenu.vue';
import { setAuthorizationHeaders } from '@/api/utils.ts';
import { useGoTo } from 'vuetify/framework';

const props = defineProps({
  document: {
    type: Object as PropType<Document>,
    default: undefined
  },
  streamed: {
    type: Boolean,
    default: false
  }
});

const { t, locale } = useI18n();
const { deepAAPI } = useApi();

const settingsMenu = ref(false);

// Must be a ref because it's bound via `v-model:settings`.
const settings = ref({
  language: locale.value,
  nWords: 150,
  replaceSwissCharacters: true
});

const summarization = ref<DeepAResponseString>();
const summarizationError = ref();

const summarizationContainerRef = useTemplateRef('summarizationContainerRef');
const goTo = useGoTo();

// Function to scroll the container inside the dialog
function onSummarizationContainerScrollDown() {
  nextTick(() => {
    if (summarizationContainerRef.value) {
      // Scroll to the summarizationContainerRef
      goTo(summarizationContainerRef.value.$el.scrollHeight, {
        duration: 0,
        offset: 10,
        easing: 'easeInOutCubic',
        container: summarizationContainerRef.value.$el
      });
    }
  });
}

const isLoading = ref(false);
async function onSummarizationCreate(id: string) {
  settingsMenu.value = false;
  isLoading.value = true;

  try {
    const res = await deepAAPI.value.summarizationFileDocumentIdV3.create({
      id,
      language: settings.value.language,
      nWords: settings.value.nWords,
      replaceSwissCharacters: settings.value.replaceSwissCharacters
    });
    if (res.data) {
      summarization.value = res.data;
    }
  } catch (e) {
    summarizationError.value = e;
  } finally {
    isLoading.value = false;
  }
}

async function onSummarizationCreateStreamed(id: string) {
  settingsMenu.value = false;
  summarizationError.value = undefined;
  summarization.value = undefined;

  isLoading.value = true;
  const headers = {};
  await setAuthorizationHeaders(headers);
  const events =
    await deepAAPI.value.summarizationFileDocumentIdV3.createStream(
      import.meta.env.VITE_DEEPA_API_BASE_URL,
      {
        id,
        language: settings.value.language,
        nWords: settings.value.nWords,
        replaceSwissCharacters: settings.value.replaceSwissCharacters
      },
      headers
    );

  events.onopen = () => {
    isLoading.value = false;
  };

  events.onmessage = (e) => {
    const lines = e.data?.split('\n');
    for (const line of lines) {
      if (line.startsWith('data:')) {
        try {
          const jsonString = line.slice(5).trim(); // Remove "data:" prefix

          if (!jsonString) return;

          const data = JSON.parse(jsonString);

          if (data.result === null) return;

          if (!summarization.value) {
            summarization.value = { result: data.result, events: data.events };
          } else {
            summarization.value.result += data.result;
          }
        } catch (error) {
          console.error('JSON Parsing Error:', error);
          return { data: undefined, error };
        }
      }
    }

    onSummarizationContainerScrollDown();
  };

  events.onerror = (e) => {
    summarizationError.value = e;
    console.log('DeepA: summarization error', e);
    isLoading.value = false;
    console.error(t('validation.error.error_occurred'));
    events.close();
  };
}

async function summarize() {
  if (!props.document?.documentId) return;

  if (props.streamed) {
    return onSummarizationCreateStreamed(props.document.documentId);
  } else {
    return onSummarizationCreate(props.document.documentId);
  }
}

watch(
  () => props.document,
  (newDocument: Document) => {
    if (newDocument) {
      summarize();
    }
  },
  {
    immediate: true
  }
);

defineExpose({
  summarize
});
</script>
